import { type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

import { findProductTypologies } from '../../filter.query';

import { type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';

interface UseProductTypologyDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useProductTypologiesData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  taxonomy
}: UseProductTypologyDataProps) {
  const {
    data: productTypologyData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [findProductTypologies.name, debouncedSearch, taxonomy],
    queryFn: async () => findProductTypologies({ taxonomy: unref(taxonomy) }),
    select: (value) => (value.status === 'success' ? value.data : undefined)
  });

  const itemsProductTypologies = computed(() => {
    if (!productTypologyData.value) {
      return undefined;
    }

    const productTypologyFiltered = productTypologyData.value?.children?.filter(
      (child) =>
        child.label.includes(debouncedSearch.value) ||
        checkedItemsState.value.productTypologyFilters[child.nodeId]?.state === 'checked'
    );

    if (!productTypologyFiltered) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: productTypologyData.value,
      defaultFilters: productTypologyData.value,
      selectedFilters: checkedItemsState.value.productTypologyFilters
    });
  });

  const isLoadingProductTypology = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsProductTypology: itemsProductTypologies, isLoadingProductTypology };
}
